import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  checkClauseExists,
  createClause,
  getClauseEnums,
  getClauseInfo,
  updateClause,
} from './queries';
import { LookupToList, mapToClauseInfo } from './utils';
import Loader from '@components/loader/Loader';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { isEmpty } from '@utils/validationUtils';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { getError } from '@utils/graph-utils';
import SelectFormField from '@components/form-fields/SelectFormField';
import TextInputFormField from '@components/form-fields/TextInputFormField';
import {
  IClauseInfo,
  IClausePopupLovs,
  IClausePopUpProps,
  IClausePopupState,
} from '.';
import { initialState } from './content';
import EnhancedButton, {
  EnhancedButtonStatus,
} from '@components/button/EnhancedButton';
import { useGridStyles } from '../../../../styles/sharedStyles';
import { cloneDeep } from 'lodash';
import TextEditorFormField from '@components/form-fields/TextEditorFormField';

const ClausePopUp: React.FC<IClausePopUpProps> = ({
  clauseId,
  lineId,
  isLineFieldEnabled = false,
  onSuccess,
  onClose,
}) => {
  const { classes } = useGridStyles({ fieldsPerRow: 2 });

  const [popupState, setPopupState] = useState<IClausePopupState>(initialState);
  const [lovs, setLovs] = useState<IClausePopupLovs>({
    lines: {},
    statuses: {},
  });

  const [submitButtonState, setSubmitButtonState] =
    useState<EnhancedButtonStatus>();

  const [clauseEnumsQuery, { loading: enumsLoading }] =
    useLazyQuery(getClauseEnums);
  const [clauseInfoQuery, { loading: clauseDetailsLoading }] =
    useLazyQuery(getClauseInfo);
  const [clauseAction] = useMutation(clauseId ? updateClause : createClause);
  const [checkClauseExistsLazy, { loading: validationLoading }] =
    useLazyQuery(checkClauseExists);

  const initialize = async () => {
    const newPopupState = cloneDeep(popupState);
    const newValues = newPopupState.values;

    const clauseEnums = await loadClauseEnums();
    setLovs(() => LookupToList(clauseEnums));

    newValues.clauseStatus = 'INACTIVE';
    newValues.lineId = lineId || '';

    if (clauseId) {
      const data = await loadClauseInfo();
      if (data.data) {
        const clauseEntity = mapToClauseInfo(data.data, lineId, lineId);
        if (clauseEntity) {
          newValues.lineId = clauseEntity.lineId;
          newValues.clauseName = clauseEntity.clauseName;
          newValues.clauseExternalCode = clauseEntity.clauseExternalCode;
          newValues.clauseDescription = clauseEntity.clauseDescription;
          newValues.clauseStatus = clauseEntity.clauseStatus;
        }
      }
    }

    setPopupState((prev) => ({
      ...prev,
      values: newValues,
    }));
  };

  const loadClauseEnums = async () => {
    const result = await clauseEnumsQuery({
      fetchPolicy: 'no-cache',
    });

    return result.data;
  };

  const loadClauseInfo = async () => {
    const result = await clauseInfoQuery({
      variables: { id: clauseId },
    });
    return result;
  };

  useEffect(() => {
    initialize();
  }, []);

  const handleSubmit = async () => {
    const hasErrors = await validateForm();

    if (!hasErrors) {
      setSubmitButtonState('loading');
      try {
        const values = popupState.values;
        const response = await clauseAction({
          variables: {
            clauseInputs: {
              lineID: lineId || values.lineId,
              externalCode: values.clauseExternalCode,
              name: values.clauseName,
              description: values.clauseDescription,
              status: values.clauseStatus,
            },
            ...(clauseId && { entityId: clauseId }),
          },
          errorPolicy: 'all',
        });

        if (isEmpty(response.errors)) {
          toast.success(
            <ToastSuccessMessage>
              {clauseId
                ? 'Clause successfully updated'
                : 'Clause successfully created'}
            </ToastSuccessMessage>
          );

          setTimeout(() => {
            setSubmitButtonState('success');
            onSuccess();
            onClose();
          }, 500);
        } else {
          setSubmitButtonState(undefined);
          toast.error(
            <ToastErrorMessage>{getError(response)}</ToastErrorMessage>
          );
        }
      } catch (error) {
        setSubmitButtonState(undefined);
        toast.error(
          <ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>
        );
      }
    }
  };

  const validateClauseNameExistsOnLineChange = async (
    selectedLineId?: string,
    clauseName?: string
  ) => {
    if (!isEmpty(selectedLineId) && !isEmpty(clauseName)) {
      const result = await checkClauseExistsLazy({
        variables: {
          lineId: selectedLineId,
          name: clauseName,
          clauseId: clauseId,
          externalCode: '',
        },
      });

      if (
        result.data.PlanConfigManagement?.queries?.checkClauseExists.length > 0
      ) {
        return 'Clause Name already exists under the same Line';
      }
    }

    return '';
  };

  const validateClauseExternalCodeExistsOnLineChange = async (
    selectedLineId?: string,
    clauseExternalCode?: string
  ) => {
    if (!isEmpty(selectedLineId) && !isEmpty(clauseExternalCode)) {
      const result = await checkClauseExistsLazy({
        variables: {
          lineId: selectedLineId,
          externalCode: clauseExternalCode,
          clauseId: clauseId || '',
          name: '',
        },
      });

      if (
        result?.data?.PlanConfigManagement?.queries?.checkClauseExists.length >
        0
      ) {
        return 'Clause External Code already exists under the same Line';
      }
    }

    return '';
  };

  const handleChange = async (
    fieldName: keyof IClauseInfo,
    value: IClauseInfo[keyof IClauseInfo]
  ) => {
    const { values, errors, touched } = cloneDeep(popupState);

    if (fieldName === 'lineId') {
      values.lineId = value;
      const [clauseExternalCodeError, clauseNameError] = await Promise.all([
        onCustomValidate('clauseExternalCode', values),
        onCustomValidate('clauseName', values),
      ]);

      errors.clauseExternalCode = clauseExternalCodeError;
      errors.clauseName = clauseNameError;

      touched.clauseExternalCode = true;
      touched.clauseName = true;
    }

    setPopupState((prev) => ({
      ...prev,
      values: { ...values, [fieldName]: value },
      errors: { ...errors },
      touched: { ...touched },
    }));
  };

  const onCustomValidate = async (
    fieldName: string,
    values: IClauseInfo = popupState.values
  ) => {
    if (fieldName === 'clauseExternalCode') {
      return await validateClauseExternalCodeExistsOnLineChange(
        values.lineId,
        values[fieldName]
      );
    }

    if (fieldName === 'clauseName') {
      return await validateClauseNameExistsOnLineChange(
        values.lineId,
        values[fieldName]
      );
    }

    return '';
  };

  const handleBlur = async (fieldName: string) => {
    const { values, errors } = cloneDeep(popupState);
    if (fieldName === 'clauseExternalCode' || fieldName === 'clauseName') {
      errors[fieldName] = await onCustomValidate(fieldName, values);
    }

    setPopupState((prev) => ({
      ...prev,
      touched: { ...prev.touched, [fieldName]: true },
      errors: errors,
    }));
  };

  const validateForm = async () => {
    const { values, errors, touched } = cloneDeep(popupState);

    Object.keys(values).forEach((key: keyof IClauseInfo) => {
      if (key === 'lineName' || key === 'clauseId') return;

      touched[key] = true;
      if (!values[key]) {
        errors[key] = 'Required';
      } else {
        errors[key] = '';
      }
    });

    if (!errors.clauseExternalCode) {
      errors.clauseExternalCode = await onCustomValidate(
        'clauseExternalCode',
        values
      );
    }

    if (!errors.clauseName) {
      errors.clauseName = await onCustomValidate('clauseName', values);
    }

    setPopupState((prev) => ({
      ...prev,
      touched: touched,
      errors: errors,
    }));

    return Object.values(errors).some((e) => e);
  };

  if (enumsLoading || clauseDetailsLoading) {
    return <Loader />;
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
    >
      <section className={classes.fieldsGrid}>
        <SelectFormField
          name="lineId"
          title="Line*"
          placeholder="Line"
          value={popupState.values.lineId}
          error={popupState.touched.lineId ? popupState.errors.lineId : ''}
          disabled={!isLineFieldEnabled}
          selectOptions={lovs.lines}
          onChange={(v) => handleChange('lineId', v)}
        />
        <SelectFormField
          name="clauseStatus"
          title="Status*"
          placeholder="Status"
          value={popupState.values.clauseStatus}
          error={
            popupState.touched.clauseStatus
              ? popupState.errors.clauseStatus
              : ''
          }
          selectOptions={lovs.statuses}
          onChange={(v) => handleChange('clauseStatus', v)}
        />
        <TextInputFormField
          name="clauseExternalCode"
          title="External Code*"
          placeholder="External Code"
          error={
            popupState.touched.clauseExternalCode
              ? popupState.errors.clauseExternalCode
              : ''
          }
          value={popupState.values.clauseExternalCode}
          onChange={(e) => handleChange('clauseExternalCode', e.target.value)}
          onBlur={() => handleBlur('clauseExternalCode')}
          loader={validationLoading}
        />
        <TextInputFormField
          name="clauseName"
          title="Name*"
          placeholder="Name"
          value={popupState.values.clauseName}
          error={
            popupState.touched.clauseName ? popupState.errors.clauseName : ''
          }
          onChange={(e) => handleChange('clauseName', e.target.value)}
          onBlur={() => handleBlur('clauseName')}
          loader={validationLoading}
        />
        <div className={classes.fullWidthField}>
          <TextEditorFormField
            key="note"
            name="note"
            title="Note*"
            placeholder="Enter a note..."
            value={popupState.values.clauseDescription}
            error={
              popupState.touched.clauseDescription
                ? popupState.errors.clauseDescription
                : ''
            }
            onChange={(name, value) => handleChange('clauseDescription', value)}
          />
        </div>
      </section>
      <div className={classes.submitButtonContainer}>
        <EnhancedButton isPrimary state={submitButtonState} type="submit">
          Submit
        </EnhancedButton>
      </div>
    </form>
  );
};

export default ClausePopUp;
